// apps/client/src/pages/reports/RctiCashflowPage.tsx
// RCTI CASHFLOW REPORT - Commission breakdown by run number and payment type

import React, { useState, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { resolveLoanBookSelection, getDisplayLoanBooks } from '../../utils/loanBookResolution';
import type { ResolvedLoanBook } from '@valutten/shared-schemas';
import StatCard from '../../components/common/StatCard';
import { Receipt, HandCoins, TrendingUp, MinusCircle, PiggyBank } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';
import { formatCurrency, formatCurrencyShorthand } from '../../utils/formatters';
import { COMMISSION_GST_BASIS } from '../../utils/commissionDisplay';
import { useRctiCashflow } from '../../hooks/useRctiCashflow';
import { useCompanyDataYearRange } from '../../hooks/useCompanyDataYearRange';
import RctiMatrix from '../../components/reports/RctiMatrix';
import RunDistributionChart from '../../components/charts/RunDistributionChart';

const RctiCashflowPage: React.FC = () => {
  const { userData, companyData, loanBooks = [] } = useOutletContext<any>();
  const hasEntityLoanBooks = useMemo(() =>
    (loanBooks as ResolvedLoanBook[]).some((lb: ResolvedLoanBook) => lb.type === 'entity'),
    [loanBooks]
  );
  const [selectedLoanBook, setSelectedLoanBook] = useState('all');
  const resolvedParams = useMemo(
    () => resolveLoanBookSelection(selectedLoanBook, loanBooks),
    [selectedLoanBook, loanBooks]
  );

  // ✅ DYNAMIC YEAR RANGE: Fetch from BigQuery for year dropdown population
  const { earliestYear, latestYear } = useCompanyDataYearRange({
    companyId: userData?.companyId,
  });

  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    yearType: 'calendar' as 'calendar' | 'financial',
    officeId: undefined as string | undefined,
  });

  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());

  // Generate year options for dropdown (newest first)
  // ✅ FIX (2026-01-05): Always include current year even if no data exists yet
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const dynamicLatestYear = Math.max(latestYear ?? currentYear, currentYear);
    const dynamicEarliestYear = earliestYear ?? (currentYear - 4);
    const years = [];
    for (let year = dynamicLatestYear; year >= dynamicEarliestYear; year--) {
      years.push(year);
    }
    return years;
  }, [earliestYear, latestYear]);

  // Fetch RCTI cashflow data
  const { months, loading, error, grandTotals } = useRctiCashflow({
    companyId: userData?.companyId || '',
    year: filters.year,
    yearType: filters.yearType,
    officeId: filters.officeId,
    entityCode: resolvedParams.entityCode,
  });

  // Toggle month expansion
  const toggleMonth = (month: string) => {
    const newExpanded = new Set(expandedMonths);
    if (newExpanded.has(month)) {
      newExpanded.delete(month);
    } else {
      newExpanded.add(month);
    }
    setExpandedMonths(newExpanded);
  };

  // Expand all months
  const expandAll = () => {
    setExpandedMonths(new Set(months.map((m) => m.month)));
  };

  // Collapse all months
  const collapseAll = () => {
    setExpandedMonths(new Set());
  };

  if (loading) {
    return <div className="p-8 text-center text-white">Loading RCTI cashflow report...</div>;
  }

  if (error) {
    return (
      <div className="p-8 text-center text-red-500">
        Error loading RCTI cashflow report: {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Receipt}
        title="RCTI Cashflow by Run Number"
        subtitle="Commission breakdown by run number and payment type for cashflow forecasting"
      />

      {/* Filter Controls */}
      <div className="flex gap-4 mb-8 flex-wrap">
        {/* Year Selector */}
        <div className="flex flex-col">
          <label className="text-sm text-gray-400 mb-2">Year</label>
          <select
            className="bg-gray-800 text-white px-4 py-2 rounded border border-gray-700"
            value={filters.year}
            onChange={(e) => setFilters({ ...filters, year: parseInt(e.target.value) })}
          >
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {filters.yearType === 'financial' ? `${year}/${year + 1}` : year}
              </option>
            ))}
          </select>
        </div>

        {/* Year Type Selector */}
        <div className="flex flex-col">
          <label className="text-sm text-gray-400 mb-2">Year Type</label>
          <div className="flex gap-2">
            <button
              className={`px-4 py-2 rounded ${
                filters.yearType === 'calendar'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-800 text-gray-400'
              }`}
              onClick={() => setFilters({ ...filters, yearType: 'calendar' })}
            >
              Calendar Year
            </button>
            <button
              className={`px-4 py-2 rounded ${
                filters.yearType === 'financial'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-800 text-gray-400'
              }`}
              onClick={() => setFilters({ ...filters, yearType: 'financial' })}
            >
              Financial Year
            </button>
          </div>
        </div>

        {/* Loan Book Selector — unified-mode companies only */}
        {hasEntityLoanBooks && (
          <div className="flex flex-col">
            <label className="text-sm text-gray-400 mb-2">Loan Book</label>
            <select
              className="bg-gray-800 text-white px-4 py-2 rounded border border-gray-700"
              value={selectedLoanBook}
              onChange={(e) => setSelectedLoanBook(e.target.value)}
            >
              <option value="all">All Loan Books</option>
              {getDisplayLoanBooks(loanBooks as ResolvedLoanBook[]).map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        )}

      </div>

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <StatCard
          title="Total Upfront"
          value={formatCurrencyShorthand(grandTotals.upfrontCents)}
          subtitle={`${filters.yearType === 'financial' ? `${filters.year}/${filters.year + 1}` : filters.year} ${filters.yearType} year • ${COMMISSION_GST_BASIS}`}
          icon={<HandCoins size={24} />}
          gradientColor="#3b82f6"
        />
        <StatCard
          title="Total Trail"
          value={formatCurrencyShorthand(grandTotals.trailCents)}
          subtitle={`${filters.yearType === 'financial' ? `${filters.year}/${filters.year + 1}` : filters.year} ${filters.yearType} year • ${COMMISSION_GST_BASIS}`}
          icon={<TrendingUp size={24} />}
          gradientColor="#8b5cf6"
        />
        <StatCard
          title="Total Deductions"
          value={formatCurrencyShorthand(grandTotals.otherCents)}
          subtitle={`${filters.yearType === 'financial' ? `${filters.year}/${filters.year + 1}` : filters.year} ${filters.yearType} year • ${COMMISSION_GST_BASIS}`}
          icon={<MinusCircle size={24} />}
          gradientColor="#ec4899"
        />
        <StatCard
          title="Aggregator Charges"
          value={formatCurrencyShorthand(grandTotals.aggregatorChargeCents ?? 0)}
          subtitle="System fees, programme fees, transfer fees"
          icon={<MinusCircle size={24} />}
          gradientColor="#f59e0b"
        />
        <StatCard
          title="Grand Total"
          value={formatCurrencyShorthand(grandTotals.totalCents)}
          subtitle={`${filters.yearType === 'financial' ? `${filters.year}/${filters.year + 1}` : filters.year} ${filters.yearType} year`}
          icon={<PiggyBank size={24} />}
          gradientColor="#10b981"
        />
      </div>

      {/* Run Distribution Chart */}
      {months.length > 0 && <RunDistributionChart months={months} year={filters.year} yearType={filters.yearType} />}

      {/* Monthly Breakdown Section */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-white">Monthly Breakdown</h3>
          <div className="flex gap-2">
            <button
              onClick={expandAll}
              className="px-3 py-1 text-sm rounded bg-gray-700 text-white hover:bg-gray-600 transition-colors"
            >
              Expand All
            </button>
            <button
              onClick={collapseAll}
              className="px-3 py-1 text-sm rounded bg-gray-700 text-white hover:bg-gray-600 transition-colors"
            >
              Collapse All
            </button>
          </div>
        </div>

        {months.length === 0 ? (
          <div className="bg-gray-800 p-8 rounded-lg text-center text-gray-400">
            No RCTI cashflow data found for {filters.year} {filters.yearType} year
          </div>
        ) : (
          <div className="space-y-4">
            {months.map((monthData) => {
              const isExpanded = expandedMonths.has(monthData.month);
              const monthLabel = formatMonthLabel(monthData.month);

              return (
                <div key={monthData.month} className="bg-gray-800 rounded-lg overflow-hidden">
                  {/* Month Header (Clickable) */}
                  <button
                    onClick={() => toggleMonth(monthData.month)}
                    className="w-full px-6 py-4 flex items-center justify-between bg-gray-900 hover:bg-gray-850 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-lg font-bold text-white">{monthLabel}</span>
                      <span className="text-sm text-gray-400">
                        {monthData.runs.length} run{monthData.runs.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-6">
                      <span className="text-sm text-gray-400">
                        Total: <span className="text-white font-semibold">{formatCurrency(monthData.totals.totalCents)}</span>
                      </span>
                      <svg
                        className={`w-5 h-5 text-gray-400 transition-transform ${
                          isExpanded ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>

                  {/* Month Content (Expandable) */}
                  {isExpanded && (
                    <div className="p-6">
                      <RctiMatrix runs={monthData.runs} monthTotals={monthData.totals} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Methodology Note */}
      <div className="mt-8 p-4 bg-gray-800 rounded-lg border border-gray-700">
        <h3 className="text-sm font-semibold text-gray-300 mb-2">Report Methodology</h3>
        <p className="text-sm text-gray-400">
          This report shows commission breakdown by run number (1, 2, 3) and payment type for cashflow forecasting.
          Run numbers are determined by RCTI payment dates: Days 1-10 = Run 1, Days 11-20 = Run 2, Days 21-31 = Run 3.
          Payment types are normalized across all templates: Upfront includes upfront commissions only, Trail includes trail/arrears/discharged commissions,
          and Deductions includes referrer/broker deductions, clawbacks, fees, and adjustments.
        </p>
      </div>
    </div>
  );
};

/**
 * Format month string (YYYY-MM) to readable label
 */
function formatMonthLabel(month: string): string {
  const [year, monthNum] = month.split('-');
  const date = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

export default RctiCashflowPage;
